import { useRef } from 'react'
import {
  toast,
  ToastContainer,
  ToastContainerProps,
  Bounce,
} from 'react-toastify'
import { Close, Flags } from 'components/icons'
import { styled } from 'theme'
import { Button, Text } from 'components/elements'
import 'react-toastify/dist/ReactToastify.css'
import { GroupIconCss, SvgCircleWrap, SvgWrap } from 'components/svg'

const CustomCloseButton = () => (
  <Button
    size="sm"
    color={'transparent'}
    className="toastClose"
    transparent="asIcon"
    type="button"
    onClick={() => toast.dismiss()}
  >
    <SvgWrap color={'gray'} size="sm">
      <Close />
    </SvgWrap>
  </Button>
)

const useToast = () => {
  const successToastIdRef = useRef<number | string | null>(null)
  const errorToastIdRef = useRef<number | string | null>(null)

  const showToast = (
    message: string,
    type: 'success' | 'error',
    options?: ToastContainerProps
  ) => {
    let currentToastIdRef =
      type === 'success' ? successToastIdRef : errorToastIdRef
    if (
      currentToastIdRef.current &&
      toast.isActive(currentToastIdRef.current)
    ) {
      // Update the existing toast
      toast.update(currentToastIdRef.current, {
        render: (
          <>
            <Text
              as="h5"
              className={`toastText toastText${
                type === 'success' ? 'Success' : 'Error'
              }`}
            >
              {message}
            </Text>
          </>
        ),
        closeButton: <CustomCloseButton />,
        ...options,
      })
    } else {
      // Create a new toast
      const defaultOptions: ToastContainerProps = {
        position: 'bottom-left',
        transition: Bounce,
        autoClose: 5000,
        icon: (
          <SvgCircleWrap
            color={type}
            css={{
              ...GroupIconCss,
              ...(type === 'success' ? GroupIconSuccessCss : GroupIconErrorCss),
              ml: '-$space$4',
              my: '-$space$4',
            }}
          >
            <Flags />
          </SvgCircleWrap>
        ),
        hideProgressBar: false,
        newestOnTop: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: type === 'success' ? 'toastSuccess' : 'toastError',
        closeButton: <CustomCloseButton />,
      }
      const toastOptions: ToastContainerProps = {
        ...defaultOptions,
        ...options,
      }
      currentToastIdRef.current =
        type === 'success'
          ? toast.success(
              <>
                <Text as="h5" className={`toastText toastTextSuccess`}>
                  {message}
                </Text>
              </>,
              toastOptions
            )
          : toast.error(
              <>
                <Text as="h5" className={`toastText toastTextError`}>
                  {message}
                </Text>
              </>,
              toastOptions
            )
    }
  }

  const showSuccessToast = (message: string, options?: ToastContainerProps) =>
    showToast(message, 'success', options)
  const showErrorToast = (message: string, options?: ToastContainerProps) =>
    showToast(message, 'error', options)

  return {
    success: showSuccessToast,
    error: showErrorToast,
  }
}

export default useToast

const GroupIconSuccessCss = {
  mr: '$space$20',
  background: '$successBg',
  borderColor: '$success50',
}

const GroupIconErrorCss = {
  mr: '$space$20',
  background: '$errorBg',
  borderColor: '$error50',
}

export const Toast = styled(ToastContainer, {
  '&.toastContainer': {
    width: '100%',
    maxWidth: 475,
    p: 0,
  },
  '.Toastify': {
    '&__toast': {
      'border': '1px dashed',
      'p': '$space$12',
      'pb': '$space$18',
      '&-icon': {
        width: 'auto',
        m: 0,
      },
      '&-body': {
        p: 0,
      },
    },
    '&__progress-bar': {
      '&--error': {
        background: '$error400',
      },
      '&--success': {
        background: '$success400',
      },
    },
  },
  '.toast': {
    '&Text': {
      'fontWeight': '$semibold',
      '&Error': {
        color: '$error',
      },
      '&Success': {
        color: '$success',
      },
    },
    '&Success, &Error': {
      background: '$error25',
      borderRadius: '$size$12',
      borderColor: '$error300',
      m: 0,
      svg: {
        m: 0,
        path: {
          stroke: '$error',
        },
      },
    },
    '&Success': {
      background: '$success25',
      borderColor: '$success300',
      svg: {
        path: {
          stroke: '$success',
        },
      },
    },
    '&Close': {
      alignItems: 'flex-start',
      ml: '$space$10',
    },
  },
})
